import Phaser from 'phaser';
import { WORLDS, isWorldUnlocked, type World } from '@/data/worlds';
import { isDevModeEnabled } from '@/systems/DevMode';
import { saveSystem } from '@/systems/SaveSystem';
import { addBackdrop } from '@/ui/background';
import { Button } from '@/ui/components/Button';
import { MuteToggle } from '@/ui/components/MuteToggle';
import { Layout, Palette, Typography } from '@/ui/theme';
import { fadeInScene, fadeToScene } from '@/utils/transitions';

/**
 * WorldMapScene — galeria dos 6 mundos.
 *
 * Mundos desbloqueados levam para LevelMapScene. Mundos sem fases
 * (placeholders das fases 2..6) aparecem como "Em breve" e não são
 * clicáveis ainda. Mundos não desbloqueados aparecem como cadeado.
 */
export class WorldMapScene extends Phaser.Scene {
  constructor() {
    super({ key: 'WorldMapScene' });
  }

  create(): void {
    addBackdrop(this);
    fadeInScene(this);
    const { width, height } = this.scale;

    this.add
      .text(width / 2, 60, 'Mapa de Mundos', {
        fontFamily: Typography.fontFamily,
        fontSize: '48px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, 110, 'Escolha seu próximo desafio geek', {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5);

    this.renderGrid();

    new Button(this, 96, height - 48, 'Voltar', {
      width: 160,
      height: 48,
      fontSize: 18,
      fillColor: 0x2a2a4a,
      hoverColor: Palette.primary,
      onClick: () => fadeToScene(this, 'MenuScene'),
    });

    new MuteToggle(this, width - 48, 48);

    // Quando o modo dev está ativo, todos os mundos aparecem destravados.
    // Sem indicação visível aqui, dá pra confundir com bug — então o
    // badge fica sempre próximo ao topo enquanto o modo estiver ligado.
    if (isDevModeEnabled()) {
      this.add
        .text(width / 2, 154, '🔓 Modo Dev — desbloqueio real desativado', {
          fontFamily: Typography.fontFamily,
          fontSize: '14px',
          color: Palette.warningCss,
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
    }
  }

  private renderGrid(): void {
    const { width, height } = this.scale;
    const { starsPerLevel } = saveSystem.getData();
    const cols = 3;
    const cardWidth = 320;
    const cardHeight = 180;
    const gapX = 36;
    const gapY = 32;

    const totalWidth = cols * cardWidth + (cols - 1) * gapX;
    const startX = width / 2 - totalWidth / 2;
    const startY = height * 0.28;

    WORLDS.forEach((world, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = startX + col * (cardWidth + gapX) + cardWidth / 2;
      const y = startY + row * (cardHeight + gapY) + cardHeight / 2;
      this.renderCard(world, x, y, cardWidth, cardHeight, isWorldUnlocked(world, starsPerLevel));
    });
  }

  private renderCard(world: World, x: number, y: number, w: number, h: number, unlocked: boolean): void {
    const playable = unlocked && world.levels.length > 0;
    const baseColor = playable ? this.parseHexCss(world.accentCss) : 0x2a2a4a;
    const titleColor = playable ? Palette.textPrimaryCss : Palette.textMutedCss;
    const flavorColor = playable ? Palette.textPrimaryCss : Palette.textDimCss;

    const bg = this.add.graphics();
    bg.fillStyle(baseColor, 1);
    bg.fillRoundedRect(x - w / 2, y - h / 2, w, h, Layout.cornerRadius);
    if (!playable) {
      bg.fillStyle(0x000000, 0.35);
      bg.fillRoundedRect(x - w / 2, y - h / 2, w, h, Layout.cornerRadius);
    }

    this.add
      .text(x - w / 2 + 24, y - h / 2 + 24, `Mundo ${world.id}`, {
        fontFamily: Typography.fontFamily,
        fontSize: '18px',
        color: Palette.textMutedCss,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0);

    this.add
      .text(x - w / 2 + 24, y - h / 2 + 56, world.name, {
        fontFamily: Typography.fontFamily,
        fontSize: '28px',
        color: titleColor,
        fontStyle: 'bold',
        wordWrap: { width: w - 48 },
      })
      .setOrigin(0, 0);

    // Mensagem de bloqueio cita o pré-requisito explícito pra o jogador
    // saber o que precisa fazer (pedagogia §7: progressão visível).
    const lockedHint = world.id > 1 ? `🔒 Tire 2★ em todas as fases do Mundo ${world.id - 1}` : '🔒 Bloqueado';
    const status = !unlocked ? lockedHint : world.levels.length === 0 ? 'Em breve' : world.theme;
    this.add
      .text(x - w / 2 + 24, y + h / 2 - 36, status, {
        fontFamily: Typography.fontFamily,
        fontSize: '16px',
        color: flavorColor,
        wordWrap: { width: w - 48 },
      })
      .setOrigin(0, 0);

    if (playable) {
      const hit = this.add.rectangle(x, y, w, h, 0xffffff, 0).setInteractive({ useHandCursor: true });
      hit.on(Phaser.Input.Events.POINTER_OVER, () => {
        bg.clear();
        bg.lineStyle(3, Palette.textPrimary, 1);
        bg.strokeRoundedRect(x - w / 2, y - h / 2, w, h, Layout.cornerRadius);
        bg.fillStyle(baseColor, 1);
        bg.fillRoundedRect(x - w / 2, y - h / 2, w, h, Layout.cornerRadius);
      });
      hit.on(Phaser.Input.Events.POINTER_OUT, () => {
        bg.clear();
        bg.fillStyle(baseColor, 1);
        bg.fillRoundedRect(x - w / 2, y - h / 2, w, h, Layout.cornerRadius);
      });
      hit.on(Phaser.Input.Events.POINTER_UP, () => {
        fadeToScene(this, 'LevelMapScene', { worldId: world.id });
      });
    }
  }

  /** Converte "#RRGGBB" → 0xRRGGBB para a API de Graphics. */
  private parseHexCss(css: string): number {
    return parseInt(css.replace('#', ''), 16);
  }
}
